import {ViewMode} from "./HardwareViewMode";
import {
    Checkbox,
    DialogContent,
    DialogTitle, FormControl, FormControlLabel, List,
    ListItemButton,
    ListItemIcon, ListItemText,
    MenuItem, Switch, Tab, Tabs
} from "@mui/material";
import {Dialog2} from "../../../../misc/Dialog2";
import {SmoothActionBar} from "../../../../misc/SmoothActionBar";
import {useAsync2, useAsyncAction} from "nate-react-api-helpers";
import React, {useEffect, useRef, useState} from "react";
import {SelectWithLabel} from "./SelectWithLabel";
import {useCurrentContractor} from "../CurrentContractor";
import {Company} from "../../../../api/Companies";
import {Contact} from "../../../../api/Contacts";
import {TabWrapper} from "../../shopdrawing/TabWrapper";
import {tabGroup} from "../../../logistics/purchasing/PartsToSource";
import {OpeningsTab, OpeningWithSelected} from "../../shopdrawing/openings/pdf/OpeningsTab";
import {api} from "../../../../api/API";
import {getAll} from "../../../logistics/routes/useGetAll";
import {useProjectId} from "../../shopdrawing/ProjectName";
import {PreambleTab} from "../../shopdrawing/openings/pdf/PreambleTab";
import {useSubmittalPreferences} from "../../../../misc/pdf/useSubmittalPreferences";
import {useAlternative} from "../alternative/Alternative";
import {useHwAPI} from "../../shopdrawing/hardware/HardwareGeneric";
import {useSyncedRef} from "../../../../misc/SyncedRef";
import {useReloader} from "../../shopdrawing/hardware/ReloadContext";
import {PhaseFilterSelect} from "../../shopdrawing/openings/PhaseFilterSelect";
import {phaseId} from "../../../../misc/Phase";

export type HardwarePageItem = "schedule" | "finishes" | "cutsheets";
const defaultPages = ["schedule", "finishes", "cutsheets"] as HardwarePageItem[]
export const defaultHardwareSchedulePages = defaultPages;

// persisted hardware-schedule preferences (mirrors the shop-drawing prefs). Versioned so
// more can be added later.
type HardwareSchedulePref = {version: 1; preamble: string; pages: HardwarePageItem[]; viewMode: ViewMode; splitByPhase?: boolean; phaseFilter?: string[]};

export function CustomizeHardwareSchedule(props: {
    onHide(): void;
    submit(config: {
        viewMode: ViewMode;
        pages: HardwarePageItem[];
        client?: Company;
        clientContact?: Contact;
        openings: OpeningWithSelected[];
        preamble: string;
        lastSubmission: string | null;
        splitByPhase: boolean;
        phases: string[];
    }): Promise<any>
}) {

    const [viewMode, setViewMode] = useState<ViewMode>("default")

    const submitRef = useRef(props.submit);
    submitRef.current = props.submit;

    const onHideRef = useRef(props.onHide);
    onHideRef.current = props.onHide;

    const {getContractorDetails, CurrentContractorPromptElement} = useCurrentContractor({
        onAutoPromptCanceled: props.onHide
    });

    const reload = useReloader();

    const hwAPI = useHwAPI();
    const projectId = useProjectId();
    const altId = useAlternative();
    const altIdRef = useSyncedRef(altId);
    const projectIdRef = useSyncedRef(projectId);

    const update = useAsyncAction(async (input) => {
        if(input.autoClean) {
            await hwAPI.merge({
                project: projectIdRef.current,
                alternative: altIdRef.current,
                groups: [],
                resequence: true,
                autoMerge: true,
            });

            reload.current();

            let list: any[] = [];

            if(input.alternative) {
                list = await getAll(null, offset => api.quoteOpenings.list({
                    offset,
                    project: input.project,
                    alternative: input.alternative,
                }), {useLazy: true});
            } else {
                list = await getAll(null, offset => api.openings.list({
                    offset,
                    project: input.project,
                }), {useLazy: true});
            }

            list = list.map(l => ({
                ...l,
                selected: input.openings.find(o => o.id === l.id)?.selected ?? true,
            }));

            input.openings = list;
        }

        const {client, clientContact} = await getContractorDetails();
        await submitRef.current({...input, client, clientContact})
        onHideRef.current()
    }, [getContractorDetails]);

    const [pages, setPages] = useState(defaultPages);
    const [splitByPhase, setSplitByPhase] = useState(false);
    const [savedPhaseFilter, setPhaseFilter] = useState<string[]>([]);
    const [tab, setTab] = useState("pages");
    const [preamble, setPreamble] = useState("")
    const [openings, setOpenings] = useState<OpeningWithSelected[]>([]);

    // a saved phase that no opening has anymore is ignored
    const phaseFilter = savedPhaseFilter.filter(id => openings.some(o => phaseId(o.phase) === id));

    const prefs = useSubmittalPreferences<HardwareSchedulePref>({
        project: projectId,
        kind: "hardware-schedule",
        onLoad: list => {
            setPreamble(list.preamble || "");
            // pages/viewMode may be absent on older saved prefs — fall back to the defaults.
            if(list.pages) setPages(list.pages);
            if(list.viewMode) setViewMode(list.viewMode);
            setSplitByPhase(!!list.splitByPhase);
            setPhaseFilter(list.phaseFilter || []);
        },
    });

    useAsync2(async (input) => {
        let list: any[] = [];

        if(input.alternative) {
            list = await getAll(null, offset => api.quoteOpenings.list({
                offset,
                project: input.project,
                alternative: input.alternative,
            }), {useLazy: true});
        } else {
            list = await getAll(null, offset => api.openings.list({
                offset,
                project: input.project,
            }), {useLazy: true});
        }

        setOpenings(list.map(l => ({
            ...l,
            selected: true,
        })));
    }, {
        project: projectId,
        alternative: altId,
    }, [projectId, altId]);

    useEffect(() => {
        if(tab === "openings" && viewMode === "default") {
            setTab("pages");
        }
    }, [tab, viewMode]);

    const [autoClean, setAutoClean] = useState(false);

    if(prefs.loading) return null;

    if(CurrentContractorPromptElement) {
        return (<>{CurrentContractorPromptElement}</>);
    }

    // persist the dialog's settings (preamble, pages, view-mode) so they're pre-filled next
    // time (mirrors Create Shop Drawing).
    const savePrefs = () => prefs.save({version: 1, preamble, pages, viewMode, splitByPhase, phaseFilter: savedPhaseFilter});

    const submit = () => {
        void savePrefs();
        update.callback({
            viewMode: viewMode,
            pages: pages,
            openings: openings,
            preamble,
            lastSubmission: prefs.lastSubmission,
            autoClean: autoClean,
            splitByPhase: splitByPhase,
            phases: phaseFilter,
        });
    };

    return (
        <Dialog2 open onClose={props.onHide} onEnterPress={submit}>
            <DialogTitle>Customize Hardware Schedule</DialogTitle>
            <TabWrapper>
                <Tabs className={tabGroup} value={tab} onChange={(e, value) => setTab(value)}>
                    <Tab label="Pages" value="pages" />
                    <Tab label="Preamble" value="preamble" />
                    {viewMode === "by-opening" && <Tab label="Openings" value="openings" />}
                </Tabs>
            </TabWrapper>
            <DialogContent style={{padding: tab === "pages" ? 0 : undefined}}>
                {tab === "pages" && <PageList pages={pages} setPages={setPages} viewMode={viewMode} setViewMode={setViewMode} splitByPhase={splitByPhase} setSplitByPhase={setSplitByPhase}
                    phases={openings.map(o => o.phase)} phaseFilter={phaseFilter} setPhaseFilter={setPhaseFilter} />}
                {tab === "preamble" && <PreambleTab value={preamble} onChange={setPreamble} />}
                {tab === "openings" && <OpeningsTab value={openings} onChange={setOpenings} />}
            </DialogContent>
            <SmoothActionBar
                loading={update.loading}
                error={update.error}
                actionName="Submit"
                onCancel={() => {
                    void savePrefs();
                    props.onHide();
                }}
                onSubmit={submit}
                leftOfButtons={<FormControl>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={autoClean}
                                onChange={e => setAutoClean(e.target.checked)}
                            />
                        }
                        label="Auto-clean groups before printing"
                    />
                </FormControl>}
            />
        </Dialog2>
    )
}


function PageList(props: {
    viewMode: ViewMode;
    setViewMode(mode: ViewMode): void;
    pages: HardwarePageItem[];
    setPages(pages: HardwarePageItem[]): void;
    splitByPhase: boolean;
    setSplitByPhase(value: boolean): void;
    phases: string[];
    phaseFilter: string[];
    setPhaseFilter(value: string[]): void;
}) {
    const {pages, setPages, viewMode, setViewMode} = props;

    return (
        <List dense>
            <PageListItemEl value="schedule" label="Hardware Schedule" pages={pages} setPages={setPages} />

            {pages.includes("schedule") && <>
                <div style={{paddingLeft: 64, paddingRight: 24}}>
                    <SelectWithLabel size="small" label="Mode" fullWidth value={viewMode} onChange={e => setViewMode(e.target.value as any)}>
                        <MenuItem value="default">Default</MenuItem>
                        <MenuItem value="by-opening">By Opening</MenuItem>
                    </SelectWithLabel>
                </div>
                <ListItemButton onClick={() => props.setSplitByPhase(!props.splitByPhase)} dense style={{paddingLeft: 64}}>
                    <ListItemIcon>
                        <Checkbox
                            edge="start"
                            checked={props.splitByPhase}
                            tabIndex={-1}
                            disableRipple
                        />
                    </ListItemIcon>
                    <ListItemText primary="Break down by phase" />
                </ListItemButton>
            </>}

            <PageListItemEl value="finishes" label="Finishes" pages={pages} setPages={setPages} />
            <PageListItemEl value="cutsheets" label="Cut Sheets" pages={pages} setPages={setPages} />

            <div style={{paddingLeft: 16, paddingRight: 24, paddingTop: 8, paddingBottom: 8}}>
                <PhaseFilterSelect phases={props.phases} value={props.phaseFilter} onChange={props.setPhaseFilter} />
            </div>
        </List>
    )
}

function PageListItemEl(props: {
    value: HardwarePageItem;
    label: string;
    pages: HardwarePageItem[];
    setPages(pages: HardwarePageItem[]): void;
}) {
    return (
        <ListItemButton onClick={() => {
            props.setPages(props.pages.includes(props.value) ? props.pages.filter(p => p !== props.value) : [...props.pages, props.value]);
        }} dense>
            <ListItemIcon>
                <Checkbox
                    edge="start"
                    checked={props.pages.includes(props.value)}
                    tabIndex={-1}
                    disableRipple
                />
            </ListItemIcon>
            <ListItemText primary={props.label} />
        </ListItemButton>
    )
}